import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Asset } from '../../../../model/asset';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Tenant } from '../../../../model/tenant';
import { Organization } from '../../../../model/organization';
import { AssetRepresentation } from '../../../../model/asset-representation';
import { TenantsService } from '../../../../services/tenants.service';
import { OrganizationsService } from '../../../../services/organizations.service';
import { MapService } from '../../../../services/map.service';
import * as mapboxgl from 'mapbox-gl';
import { JsonEditorService } from '../../../../services/json-editor.service';
import { JsonEditorComponent } from 'ang-jsoneditor';
import { translate } from '@ngneat/transloco';
import { MessageService } from 'primeng/api';
import { AuthGuard } from '../../../../common/auth-guard.service';
import { LoggedUser } from '../../../../common/logged-user.service';
import { TimezoneService } from '../../../../services/timezone.service';
import {noSpecialCharacters, noWhitespaces} from '../../../../shared/custom-validators';
import {Template} from "../../../../model/template";
import {TemplatesService} from "../../../../services/templates.service";
import {TemplateFilters} from "../../../../model/filters/template-filters";
import {DefaultAssetRepresentation} from "../../../../model/default-asset-representation";
import {DefaultAssetRepresentationsService} from "../../../../services/default-asset-representations.service";
import {HwCatalogService} from "../../../../services/hw-catalog.service";


@Component({
  selector: 'app-assets-form-info',
  templateUrl: './assets-form-info.component.html',
  styleUrls: ['./assets-form-info.component.scss']
})
export class AssetsFormInfoComponent implements OnInit, OnDestroy {

  public readonly DEFAULT_ZOOM = 14;
  public readonly DEFAULT_LATITUDE = 39.984255;
  public readonly DEFAULT_LONGITUDE = -0.027077;

  @Input() mode: 'create' | 'edit';

  @Input() asset: Asset;
  @Input() template: Template;
  @Input() defaultAssetRepresentation: DefaultAssetRepresentation;

  @Output() assetChange = new EventEmitter<Asset>();
  @Output() templateChange = new EventEmitter<Template>();
  @Output() defaultAssetRepresentationChange = new EventEmitter<DefaultAssetRepresentation>();
  @Output() onNextStep = new EventEmitter<void>();
  @Output() onCancel = new EventEmitter<void>();

  @Output() onTenantChange = new EventEmitter<void>();
  @Output() onOrganizationChange = new EventEmitter<void>();

  assetForm: FormGroup;
  tenantSelected: boolean;
  templateDisabled: boolean = false;

  timezones: any[];
  tenants: Tenant[];
  organizations: Organization[];
  assetRepresentations: AssetRepresentation[];
  defaultAssetRepresentations: DefaultAssetRepresentation[];
  templates: Template[];
  currentTemplateFilters: TemplateFilters;

  previousTemplateUuid: string;
  assetRepresentationDisabled: boolean = false;
  defaultAssetRepresentationDisabled: boolean = false;

  map: mapboxgl.Map;
  marker: mapboxgl.Marker;

  technicalDetailsVisible = false;

  isDestroyed = false;

  @ViewChild('technicalDetailsEditor', {static: false})
  technicalDetailsEditor: JsonEditorComponent;

  constructor(private mapService: MapService,
              private formBuilder: FormBuilder,
              public jsonEditorService: JsonEditorService,
              private tenantsService: TenantsService,
              private hwCatalogService: HwCatalogService,
              private organizationsService: OrganizationsService,
              private templatesService: TemplatesService,
              private defaultAssetRepresentationsService: DefaultAssetRepresentationsService,
              private authGuard: AuthGuard,
              private loggedUser: LoggedUser,
              private timezoneService: TimezoneService,
              private messageService: MessageService) {

    this.currentTemplateFilters = new TemplateFilters();
  }

  ngOnInit(): void {
    this.timezones = this.timezoneService.getAllTimezones();
    this.previousTemplateUuid = this.template ? this.template.uuid : null;
    this.currentTemplateFilters.types = ['ASSET'];
    this.loadAndCreateForm();
  }

  buildAndConfigureMap(): void {
    const latitude = this.asset.latitude ? this.asset.latitude : this.DEFAULT_LATITUDE;
    const longitude = this.asset.longitude ? this.asset.longitude : this.DEFAULT_LONGITUDE;
    this.setAssetLocation(latitude, longitude);

    this.map = this.mapService.buildMap(this.DEFAULT_ZOOM, latitude, longitude);

    this.map.on('click', (e) => {
      this.setAssetLocation(e.lngLat.lat, e.lngLat.lng);
    });

    this.marker = this.mapService.buildStaticPulsingDot(this.map, this.assetLocation[1], this.assetLocation[0]);
  }

  setAssetLocation(latitude: number, longitude: number): void {
    this.assetForm.get('latitude').setValue(latitude);
    this.assetForm.get('longitude').setValue(longitude);
  }

  onLocationInputChange(): void {
    this.marker?.setLngLat(this.assetLocation);
    this.map.easeTo({ center: this.assetLocation, duration: 200 });
  }

  private loadAndCreateForm(): void {
    const promises = [this.tenantsService.getTenantSummaries().toPromise()];
    if (this.authGuard.superAdminRequired()) {
      promises.push(this.defaultAssetRepresentationsService.getDefaultAssetRepresentations().toPromise());
    }
    Promise.all(promises).then(response => {
      this.tenants = response[0].tenants;
      if (!this.authGuard.superAdminRequired()) {
        this.asset.tenant = this.tenants[0];
      }
      if (this.authGuard.superAdminRequired()) {
        this.defaultAssetRepresentations = response[1].defaultAssetRepresentations
          .sort((a, b) => a.name > b.name ? 1 : -1);
      }
      this.createForm(this.asset);
    });
  }

  private createForm(asset: Asset): void {
    const tenant = asset.tenant ? {name: asset.tenant.name, uuid: asset.tenant.uuid} : null;
    const organization = asset.organization ? {name: asset.organization.name, uuid: asset.organization.uuid} : null;

    this.assetForm = this.formBuilder.group({
      alias: [ asset.alias, [
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters,
        noWhitespaces
      ]],
      description: [asset.description, Validators.maxLength(255)],
      latitude: [asset.latitude, Validators.required],
      longitude: [asset.longitude, Validators.required],
      timezone: [asset.timezone],
      technicalDetails: [asset.technicalDetails],
      assetRepresentation: [{value: asset.assetRepresentation, disabled: true}],
      tenant: [tenant, Validators.required],
      organization: [{value: organization, disabled: true}, Validators.required],
      uuid: [asset.uuid]
    });

    this.assetForm.markAsPending();

    if (this.mode === 'edit') {
      this.assetForm.get('tenant').disable();
    }

    if (this.asset.tenant) {
      this.loadData(this.asset.tenant.uuid).then(() => {
        if (this.asset.assetRepresentation) {
          this.defaultAssetRepresentationDisabled = true;
        }
        if (!this.authGuard.superAdminRequired()) {
          this.assetForm.get('tenant').disable();
          if (!this.defaultAssetRepresentation) {
            this.assetRepresentationDisabled = false;
          }
          this.templateDisabled = false;
          this.tenantSelected = true;
          this.loggedUser.userObservable.subscribe(user => {
            if (user.organization) {
              this.tenantsService.getTenantOrganizationSummaries(user.tenant).subscribe(response => {
                this.organizations = response.organizations;
                this.assetForm.get('organization').setValue(this.organizations[0]);
              });
            } else {
              this.assetForm.get('organization').enable();
            }
          });
        } else {
          this.enableTenantDependentElements();
        }
      });
    } else {
      this.templateDisabled = true;
    }

    this.buildAndConfigureMap();
  }

  tenantChanged(event): void {
    this.onTenantChange.emit();
    if (event.value) {
      this.tenantSelected = true;
      this.assetForm.get('organization').setValue(null);
      this.template = null;
      this.loadData(event.value.uuid).then(() => {
        this.enableTenantDependentElements();
      });
    }
  }

  defaultAssetRepresentationChanged(event: any): void {
    if (event.value) {
      this.assetRepresentationDisabled = true;
    } else {
      if (this.tenantSelected) {
        this.assetRepresentationDisabled = false;
      }
    }
  }

  assetRepresentationChanged(event: any): void {
    this.defaultAssetRepresentationDisabled = !!event.value;
  }

  private loadData(tenantUuid: string): Promise<void> {
    this.disableTenantDependentElements();
    this.currentTemplateFilters.tenantUuids = [tenantUuid];
    return Promise.all([
      this.tenantsService.getTenantOrganizationSummaries(tenantUuid).toPromise(),
      this.tenantsService.getTenantAssetRepresentations(tenantUuid).toPromise(),
      this.templatesService.getTemplateSummaries(this.currentTemplateFilters).toPromise()
    ]).then(response => {
      this.organizations = response[0].organizations;
      this.assetRepresentations = response[1].assetRepresentations
        .sort((a, b) => a.name > b.name ? 1 : -1);
      this.templates = response[2].templates
        .sort((a, b) => a.name > b.name ? 1 : -1);
    });
  }

  organizationChanged(): void {
    this.onOrganizationChange.emit();
  }

  nextStep(): void {
    if (!this.isTechnicalDetailsValid()) {
      const summary = translate('error.summary.configErrors');
      const detail = translate('error.jsonEditor.configErrors');
      this.messageService.add({severity: 'error', summary, detail, sticky: true});
    } else if (this.assetForm.valid) {
      if ((!this.template && this.previousTemplateUuid) || (this.template && this.template.uuid != this.previousTemplateUuid)) {
        this.templateChange.emit(this.template);
      }
      this.defaultAssetRepresentationChange.emit(this.defaultAssetRepresentation);
      this.assetChange.emit(this.assetForm.getRawValue());
      this.onNextStep.emit();
    }
  }

  private isTechnicalDetailsValid(): boolean {
    return this.technicalDetailsEditor.isValidJson();
  }

  private enableTenantDependentElements(): void {
    this.tenantSelected = true;
    this.templateDisabled = false;
    this.assetForm.get('organization').enable();
    if (!this.defaultAssetRepresentation) {
      this.assetRepresentationDisabled = false;
    }
  }

  private disableTenantDependentElements(): void {
    this.tenantSelected = false;
    this.templateDisabled = true;
    this.assetForm.get('organization').disable();
    this.assetRepresentationDisabled = true;
  }

  get assetLocation(): [number, number] {
    return [this.assetForm.get('longitude').value, this.assetForm.get('latitude').value];
  }

  cancelAction(): void {
    this.onCancel.emit();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.map.remove();
  }

}
